import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { I18NextPipe } from "angular-i18next";
import { Subject } from "rxjs";
import { labelColorScheme } from "src/app/enums";
import { label, labelData, gridItem } from "src/app/models";
import { PercentageFields, TimeObject } from "../../widget-helpers/helpers";

@Component({
    selector: 'app-label',
    templateUrl: './label.component.html',
    styleUrls: ['./label.component.scss'],
    standalone: false
})

export class LabelComponent implements OnInit {
    @Input() isInWizard: boolean;
    @Input() data: any;
    @Input() alarmItems: any;
    @Input() propsWithIds: any;
    @Input() gridItem: any;
    @Input() type: string;
    @Input() colorSchemeId: number;
    @Input() notifier: Subject<any>;

    public label: label = {
        data: []
    };

    public alarmProps: any = [];
    public alarmTitle: string = "";

    tooltipOptions = {
        "contentType": "template",
        "tooltipClass": "alarmClass",
        "placement": "bottom",
        "hideDelay": 100,
        "offset": 10,
        "shadow": false
    }

    titleFontSize: number;
    valueFontSize: number;
    titleColor: string;
    valueColor: string;

    constructor(private i18nextPipe: I18NextPipe) { }

    ngOnInit(): void {
        this.notifier.subscribe(value => {
            setTimeout(() => {
                this.initializeLabel();
            }, 0)
        });
    }

    initializeLabel(): void {
        const type = this.type;
        const gridItem = this.gridItem;
        const data = this.data;
        const queueAlarmItems = this.alarmItems;
        const isInWizard = this.isInWizard;
        let theme = gridItem.colorScheme ? labelColorScheme[gridItem.colorScheme].toLowerCase() : "default";

        const alarmMapper = {
            "SLA score": "Sla",
            "Total in queue": "QueueSize",
            "Agents logged on (ex pause)": "AgentsLoggedNoPause",
            "Hold time maximum": "WaitTimeMax",
            "Agents logged on": "AgentsLoggedIn",
            "Agents in pause": "AgentsInPause",
            "Total callback in queue": "QueueSizeCiq",
            "Answer %": "AnswerRate",
            "Answer % within SLA": "AnswerRateWithinSla",
            "Total Offered": "CallsOfferedToday",
        };

        const setupKeys = Object.keys(gridItem.setup);
        const idsArr: number[] = gridItem.setup[setupKeys[0]];
        const propertyIdsArr: number[] = gridItem.setup[setupKeys[1]];

        if (data?.length > 0) {
            let labelData: labelData[] = [];
            let alarmKeys = [];

            if (queueAlarmItems) {
                alarmKeys = Object.keys(queueAlarmItems);
            }

            let keys = Object.keys(data[0]);

            let labelClass = theme + "Label";
            let propertyClass = theme.toLowerCase() === "default" || theme.toLowerCase() === "ash" ? "darkLabelKey" : "whiteLabelKey";
            let valueClass = theme.toLowerCase() === "default" || theme.toLowerCase() === "ash" ? "darkLabelValue" : "whiteLabelValue";

            if (isInWizard) {
                labelClass = labelClass + "Wizard";
                propertyClass = propertyClass + "Wizard";
                valueClass = valueClass + "Wizard";
            }

            if (idsArr?.length === 1 && propertyIdsArr?.length === 1) {
                let filteredKey = keys.filter(function (value, index, arr) {
                    return value.toLowerCase() !== "id" &&
                        value.toLowerCase() !== "name" &&
                        value.toLowerCase() !== "isodd" &&
                        value.toLowerCase() !== "alarmflag";
                })[0];

                let property = data[0]["Name"];
                let id = data[0]["Id"];
                let value = data[0][filteredKey] ? data[0][filteredKey] : 0;
                if (filteredKey === "StatusDetails") {
                    value = this.i18nextPipe.transform("dashboard:" + value);
                }
                if (TimeObject[type].hasOwnProperty(filteredKey)) {
                    value = TimeObject[type][filteredKey](value);
                }

                if (PercentageFields.indexOf(filteredKey) > -1) {
                    value = value + "%";
                }

                let hasAlarm = false;
                let alarmProps = [];
                if (queueAlarmItems) {
                    let alarmItems = queueAlarmItems[id];
                    for (let i = 0; i < alarmItems?.length; i++) {
                        let thresholdTypeName = alarmItems[i]["thresholdTypeName"];
                        let mappedProp = alarmMapper[thresholdTypeName];
                        if (mappedProp === filteredKey) {
                            hasAlarm = true;
                            let alarmPropsObj = {
                                "alarmName": alarmItems[i]["alarmName"],
                                "basicQueue": alarmItems[i]["basicQueue"],
                                "thresholdTypeName": this.i18nextPipe.transform("dashboard:" + alarmItems[i]["thresholdTypeName"]),
                                "alarmTime": alarmItems[i]["alarmTime"],
                                "class": alarmItems.length > 1 ? "popoverContentInline" : "popoverContent"
                            };

                            alarmProps.push(alarmPropsObj);
                        }
                    }
                }

                if (gridItem.colorScheme === 0) {
                    labelClass = "multicolor0";
                    propertyClass = "whiteLabelKey";
                    valueClass = "whiteLabelValue";

                    if (isInWizard) {
                        labelClass = labelClass + "Wizard";
                        propertyClass = propertyClass + "Wizard";
                        valueClass = valueClass + "Wizard";
                    }
                }

                let label: labelData = {
                    id: id,
                    property: property,
                    alarm: property,
                    value: value,
                    class: labelClass,
                    propertyClass: propertyClass,
                    valueClass: valueClass,
                    hasAlarm: hasAlarm,
                    alarmProps: alarmProps
                };

                labelData.push(label);
            }

            if (idsArr?.length > 1) {
                let filteredKey = keys.filter(function (value, index, arr) {
                    return value.toLowerCase() !== "id" &&
                        value.toLowerCase() !== "name" &&
                        value.toLowerCase() !== "isodd" &&
                        value.toLowerCase() !== "alarmflag";
                })[0];

                let multicolorIndex = 0;
                for (let i = 0; i < data.length; i++) {
                    let alarmProps = [];
                    let key = data[i]["Name"];
                    let value = data[i][filteredKey] ? data[i][filteredKey] : 0;
                    if (filteredKey === "StatusDetails") {
                        value = this.i18nextPipe.transform("dashboard:" + value);
                    }
                    if (TimeObject[type].hasOwnProperty(filteredKey)) {
                        value = TimeObject[type][filteredKey](value);
                    }

                    if (PercentageFields.indexOf(filteredKey) > -1) {
                        value = value + "%";
                    }

                    if (gridItem.colorScheme === 0) {
                        if (multicolorIndex === 24) {
                            multicolorIndex = 0;
                        }
                        labelClass = "multicolor" + multicolorIndex;
                        propertyClass = "whiteLabelKey";
                        valueClass = "whiteLabelValue";

                        if (isInWizard) {
                            labelClass = labelClass + "Wizard";
                            propertyClass = propertyClass + "Wizard";
                            valueClass = valueClass + "Wizard";
                        }

                        multicolorIndex++;
                    }

                    let id = data[i]["Id"];
                    let hasAlarm = data[i]["AlarmFlag"] === 1 ? true : false;

                    if (hasAlarm) {
                        let alarmInfo = queueAlarmItems[id];
                        for (let j = 0; j < alarmInfo.length; j++) {
                            let alarmPropsObj = {
                                "alarmName": alarmInfo[j]["alarmName"],
                                "basicQueue": alarmInfo[j]["basicQueue"],
                                "thresholdTypeName": this.i18nextPipe.transform("dashboard:" + alarmInfo[j]["thresholdTypeName"]),
                                "alarmTime": alarmInfo[j]["alarmTime"],
                                "class": alarmInfo.length > 1 ? "popoverContentInline" : "popoverContent"
                            };

                            alarmProps.push(alarmPropsObj);
                        }
                    }

                    let label: labelData = {
                        id: id,
                        property: data[i]["Name"],
                        alarm: data[i]["Name"],
                        value: value,
                        class: labelClass,
                        propertyClass: propertyClass,
                        valueClass: valueClass,
                        hasAlarm: hasAlarm,
                        alarmProps: alarmProps
                    }

                    labelData.push(label);
                }
            }

            if (propertyIdsArr?.length > 1) {
                let filteredKeys = keys.filter(function (value, index, arr) {
                    return value.toLowerCase() !== "id" &&
                        value.toLowerCase() !== "name" &&
                        value.toLowerCase() !== "isodd" &&
                        value.toLowerCase() !== "alarmflag";
                });

                let id = data[0]["Id"];

                let multicolorIndex = 0;
                for (let i = 0; i < filteredKeys.length; i++) {
                    let alarmProps = [];
                    let hasAlarm: boolean = false;
                    if (data[0]["AlarmFlag"] === 1) {
                        let prop = filteredKeys[i];
                        let alarmInfo = queueAlarmItems[id];
                        for (let j = 0; j < alarmInfo.length; j++) {
                            let thresholdTypeName = alarmInfo[j]["thresholdTypeName"];
                            let mappedProp = alarmMapper[thresholdTypeName];
                            if (mappedProp === prop) {
                                hasAlarm = true;
                                let alarmPropsObj = {
                                    "alarmName": alarmInfo[j]["alarmName"],
                                    "basicQueue": alarmInfo[j]["basicQueue"],
                                    "thresholdTypeName": this.i18nextPipe.transform("dashboard:" + alarmInfo[j]["thresholdTypeName"]),
                                    "alarmTime": alarmInfo[j]["alarmTime"],
                                    "class": "popoverContent"
                                };

                                alarmProps.push(alarmPropsObj);
                            }
                        }
                    }
                    let property = filteredKeys[i];
                    let value = data[0][filteredKeys[i]] ? data[0][filteredKeys[i]] : 0;
                    if (filteredKeys[i] === "StatusDetails") {
                        value = this.i18nextPipe.transform("dashboard:" + value);
                    }
                    if (TimeObject[type].hasOwnProperty(filteredKeys[i])) {
                        value = TimeObject[type][filteredKeys[i]](value);
                    }

                    if (PercentageFields.indexOf(filteredKeys[i]) > -1) {
                        value = value + "%";
                    }

                    if (gridItem.colorScheme === 0) {
                        if (multicolorIndex === 24) {
                            multicolorIndex = 0;
                        }
                        labelClass = "multicolor" + multicolorIndex;
                        propertyClass = "whiteLabelKey";
                        valueClass = "whiteLabelValue";

                        if (isInWizard) {
                            labelClass = labelClass + "Wizard";
                            propertyClass = propertyClass + "Wizard";
                            valueClass = valueClass + "Wizard";
                        }

                        multicolorIndex++;
                    }

                    let label: labelData = {
                        id: id,
                        property: property,
                        value: value,
                        alarm: hasAlarm ? data[0]["Name"] : "",
                        class: labelClass,
                        propertyClass: propertyClass,
                        valueClass: valueClass,
                        hasAlarm: hasAlarm,
                        alarmProps: alarmProps
                    }

                    labelData.push(label);
                }
            }

            let sortedLabels = this.sortLabels(labelData);
            this.setLabelNames(sortedLabels);
            this.setTranslations(sortedLabels);
            this.setFontsAndColors();

            const label: label = {
                data: [...sortedLabels]
            }

            this.label = label;
        }
    }

    sortLabels(labelData): any[] {
        let sortedData = [];
        let setup = this.gridItem.setup;
        if (this.propsWithIds.length === 1) {
            let ids = [];
            setup.agentPropertiesIds ? ids = setup.agentIds : ids = setup.queueIds;
            for (let i = 0; i < ids?.length; i++) {
                let label = labelData.filter(q => q.id === ids[i])[0];
                if (label) {
                    sortedData.push(label);
                }
            }
        }

        if (this.propsWithIds.length > 1) {
            let props = [];
            setup.agentPropertiesIds ? props = setup.agentPropertiesIds : props = setup.queuePropertiesIds;
            for (let i = 0; i < props?.length; i++) {
                let propWithId = this.propsWithIds.filter(p => p.id === props[i])[0];
                let prop = labelData.filter(l => l.property === propWithId.prop)[0];
                if (prop) {
                    sortedData.push(prop);
                }
            }
        }

        return sortedData;
    }

    setLabelNames(sortedLabels) {
        const preferences = this.gridItem.setup.preferences;
        if (preferences) {
            const names = preferences.names;

            if (this.propsWithIds.length > 1) {
                for (let i = 0; i < names?.length; i++) {
                    let propWithId = this.propsWithIds.filter(p => p.id === names[i].id)[0];
                    if (sortedLabels.filter(l => l.property === propWithId.prop).length > 0) {
                        sortedLabels.filter(l => l.property === propWithId.prop)[0].property = names[i].name;
                    }
                }
            } else {
                for (let i = 0; i < names?.length; i++) {
                    if (sortedLabels.filter(l => l.id === names[i].id).length > 0) {
                        sortedLabels.filter(l => l.id === names[i].id)[0].property = names[i].name;
                    }
                }
            }
        }
    }

    setTranslations(sortedLabels) {
        sortedLabels.map(l => {
            if (l) {
                l.property = this.i18nextPipe.transform("dashboard:" + l.property);
            }
        });
    }

    setFontsAndColors() {
        const preferences = this.gridItem.setup.preferences;
        this.titleFontSize = 17;
        this.valueFontSize = 40;
        this.titleColor = "";
        this.valueColor = "";
        if (preferences) {
            const fonts = preferences.fonts;
            const colors = preferences.colors;
            if (fonts?.length > 0) {
                for (let i = 0; i < fonts.length; i++) {
                    fonts[i].id === 6 ? this.titleFontSize = fonts[i].fontSize : this.valueFontSize = fonts[i].fontSize;
                }
            }

            if (colors?.length > 0) {
                for (let i = 0; i < colors.length; i++) {
                    colors[i].id === 6 ? this.titleColor = colors[i].color : this.valueColor = colors[i].color;
                }
            }
        }
    }

    showAlarm(label) {
        this.alarmTitle = "";
        this.alarmProps = [];
        this.alarmProps = [];
        let alarmProps = [];
        for (let i = 0; i < label.alarmProps.length; i++) {
            let alarmPropsObj = {
                "alarmName": label.alarmProps[i]["alarmName"],
                "basicQueue": label.alarmProps[i]["basicQueue"],
                "thresholdTypeName": label.alarmProps[i]["thresholdTypeName"],
                "alarmTime": label.alarmProps[i]["alarmTime"],
                "class": label.alarmProps.length > 1 ? "popoverContentInline" : "popoverContent"
            };

            alarmProps.push(alarmPropsObj);
        }

        this.alarmTitle = label.alarm;
        this.alarmProps = [...alarmProps];
    }
}